import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useMutation, useQuery } from 'react-query';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { MdDragIndicator, MdEdit } from 'react-icons/md';
import { FiChevronDown } from 'react-icons/fi';
import { BiPlus, BiTrash } from 'react-icons/bi';
import { TfiSave } from 'react-icons/tfi';

import { Exam, StoreExamsRequest } from 'api/exams/types';
import { Question } from 'api/questions/types';

import { fetchExamsCategories, storeExam, updateExam } from 'api/exams';
import { fetchSingleScenario } from 'api/scenarios';
import { QuestionFormModal } from './QuestionFormModal';
import { ScenarioFormModal } from './ScenarioFormModal';
import { SecurityModal } from 'components/util/SecurityModal';
import { fetchQuestionsFaster } from 'api/questions';
import { Switch } from 'components/ui/Switch';
import { Select } from 'components/ui/Select';
import { Field } from 'components/ui/Field';
import { Modal } from 'components/ui/Modal';
import { ScenarioConsolidationChoiceModal } from 'components/shared/modals/ScenarioConsolidationChoiceModal';
import { MoveScenarioBlockModal } from 'components/shared/modals/MoveScenarioBlockModal';
import { fetchGroups } from 'api/groups';
import { examSchema } from 'utils/yup';
import { isActiveClass } from 'utils';
import { fetchOrgs } from 'api/orgs';
import {
  getSplitScenarios,
  getConsolidationPreview,
  getScenarioTitleFromQuestion,
  getQuestionScenarioId,
  getSectionIndexWithScenario,
  sectionQuestionsToBlocks,
  blocksToSectionQuestions,
  normalizeSectionQuestions,
  normalizeExamSections,
  reorderQuestionsInBlock,
  type ScenarioChoice,
  type SectionBlock
} from 'utils/examScenario';
import { AddQuestionScenarioChoiceModal } from 'components/shared/modals/AddQuestionScenarioChoiceModal';
import { ExamScenarioBlock } from 'components/shared/forms/exam/ExamScenarioBlock';
import { ExamSingleQuestionBlock } from 'components/shared/forms/exam/ExamSingleQuestionBlock';

interface Props {
  exam: Exam | undefined;
  onClose: () => void;
  refetch: () => void;
}

interface FormikValues {
  name: string;
  isActive: boolean;
  category: string;
  minPassPercentage: number;
  availableTo: {
    organizations: string[];
    groups: string[];
  };
  sections: Array<{
    id: number;
    name: string;
    isActive: boolean;
    questions: Question[];
  }>;
  duration: number;
  adminNotes: string;
}

const emptySection = { id: 1, name: '', isActive: true, questions: [] };

export const CreateExamModal = ({ exam, onClose, refetch }: Props) => {
  const splitResult = exam ? getSplitScenarios(exam) : { hasSplit: false, evidence: [] };
  const [scenarioChoice, setScenarioChoice] = useState<ScenarioChoice>(() =>
    !exam ? 'update' : splitResult.hasSplit ? 'pending' : 'update'
  );

  const [collapsedSections, setCollapsedSections] = useState(1);
  const [isOpenCreateQuestionModal, setIsOpenCreateQuestionModal] = useState<{ isOpen: boolean; index?: number }>({
    isOpen: false,
    index: undefined
  });
  const [questionForUpdate, setQuestionForUpdate] = useState<Question | null>(null);

  const [isOpenSecurityModal, setIsOpenSecurityModal] = useState(false);

  const [isOpenDeleteSecurityModal, setIsOpenDeleteSecurityModal] = useState(false);
  const [deleteQuestionModal, setDeleteQuestionModal] = useState<{
    isOpen: boolean;
    sIndex?: number;
    questionId?: string;
  }>({ isOpen: false });
  const [moveBlockModal, setMoveBlockModal] = useState<{
    isOpen: boolean;
    sIndex: number;
    block: SectionBlock;
  } | null>(null);
  const [deleteBlockModal, setDeleteBlockModal] = useState<{
    sIndex: number;
    block: SectionBlock;
  } | null>(null);
  const [addQuestionScenarioModal, setAddQuestionScenarioModal] = useState<{
    question: Question;
    currentSectionIndex: number;
    targetSectionIndex: number;
    targetSectionName: string;
    scenarioTitle?: string;
  } | null>(null);
  const [scenarioIdForEdit, setScenarioIdForEdit] = useState<string | null>(null);

  const { data: scenarioForEdit } = useQuery(
    ['scenario', scenarioIdForEdit],
    () => fetchSingleScenario(scenarioIdForEdit!),
    { enabled: !!scenarioIdForEdit }
  );

  const { data: orgs } = useQuery(['organizations'], fetchOrgs);
  const { data: groups } = useQuery(['groups'], () => fetchGroups());

  const { data: examsCategories } = useQuery(['exams-categories'], fetchExamsCategories);
  const { data: questions, refetch: refetchQuestions } = useQuery(['questions'], () => fetchQuestionsFaster());

  const { mutate: storeExamMutation, isLoading: storeIsLoading } = useMutation(storeExam);
  const { mutate: updateExamMutation, isLoading: updateIsLoading } = useMutation(updateExam);

  const initialValues: FormikValues = {
    name: exam?.name || '',
    isActive: exam?.isActive || false,
    category: exam?.category.name || '',
    minPassPercentage: exam?.minPassPercentage || 50,
    availableTo: { organizations: exam?.availableTo.organizations || [], groups: exam?.availableTo?.groups || [] },
    sections: !exam
      ? [emptySection]
      : scenarioChoice === 'keep'
        ? exam.sections.map((s, i) => ({
            id: i + 1,
            isActive: s.isActive,
            name: s.name,
            questions: normalizeSectionQuestions(s.questions || [])
          }))
        : normalizeExamSections(
            exam.sections.map((s, i) => ({
              id: i + 1,
              isActive: s.isActive,
              name: s.name,
              questions: s.questions || []
            }))
          ),
    duration: exam?.duration || 60,
    adminNotes: exam?.adminNotes ?? ''
  };

  const formik = useFormik({
    initialValues,
    validationSchema: examSchema,
    onSubmit: (v) => onSubmit(v),
    enableReinitialize: true
  });

  const categoriesOptions = examsCategories?.map((c) => ({ label: c.name, value: c.name }));
  const questionsOptions = [
    { label: 'Create New Question', value: 'create-new-question' },
    ...(questions?.result
      ?.filter((q) => exam?.category?.name === q?.examCategory?.name)
      .map((q) => ({
        label: `${q?.examCategory?.name ? q.examCategory.name + ' - ' : ''}${q.question}`,
        value: q._id
      })) || [])
  ];

  const orgsOptions = [
    { label: 'All', value: 'all' },
    ...(orgs?.map(({ _id, name }) => ({ label: name, value: _id })) || [])
  ];
  const groupsOptions = groups?.result
    .filter(({ organization }) => formik.values?.availableTo?.organizations?.includes(organization._id))
    .map(({ _id, name }) => ({ label: name, value: _id }));

  const onSubmit = (values: FormikValues) => {
    const body: StoreExamsRequest = {
      ...values,
      name: values.name,
      isActive: values.isActive,
      category: examsCategories?.find((c) => c.name === values.category)?._id || '',
      sections: values.sections.map((s) => ({
        questions: s.questions?.map((q) => q._id) || [],
        isActive: s.isActive,
        name: s.name
      })),
      duration: values.duration,
      adminNotes: values.adminNotes ?? ''
    };
    console.log(body);
    if (exam) return updateExamMutation({ ...body, examId: exam?._id }, { onSuccess });
    storeExamMutation(body, { onSuccess });
  };

  const onSuccess = () => {
    refetch();
    onClose();
  };

  const onAddSection = () => {
    formik.setFieldValue('sections', [
      ...formik.values.sections,
      { ...emptySection, id: formik.values.sections.length + 1, questions: [] }
    ]);
    setCollapsedSections(formik.values.sections.length + 1);
  };

  const onRemoveSection = (index: number) => {
    const sections = [...formik.values.sections];
    sections.splice(index, 1);
    formik.setFieldValue('sections', sections);
  };

  const onDragEnd = (result: any) => {
    if (!result.destination) return;

    const { source, destination, type } = result;

    if (type === 'droppableItem') {
      const sections = Array.from(formik.values.sections);
      const [reorderedSection] = sections.splice(source.index, 1);
      sections.splice(destination.index, 0, reorderedSection);

      formik.setFieldValue('sections', sections);
    } else if (type.includes('droppableSubItem')) {
      const parentId = parseInt(result.type.split('-')[1], 10);
      const section = formik.values.sections.find((item) => item.id === parentId);
      if (!section) return;

      const blocks = sectionQuestionsToBlocks(section.questions, scenarioChoice);
      const [reorderedBlock] = blocks.splice(source.index, 1);
      blocks.splice(destination.index, 0, reorderedBlock);
      const newQuestions = blocksToSectionQuestions(blocks);

      const newSections = formik.values.sections.map((item) =>
        item.id === parentId ? { ...item, questions: newQuestions } : item
      );
      formik.setFieldValue('sections', newSections);
    } else if (type.startsWith('blockQuestions-')) {
      const parts = result.type.split('-');
      const parentId = parseInt(parts[1], 10);
      const blockIndex = parseInt(parts[2], 10);
      if (source.droppableId !== destination.droppableId) return;
      const section = formik.values.sections.find((item) => item.id === parentId);
      if (!section) return;
      const newQuestions = reorderQuestionsInBlock(
        section.questions,
        blockIndex,
        source.index,
        destination.index
      );
      const newSections = formik.values.sections.map((item) =>
        item.id === parentId ? { ...item, questions: newQuestions } : item
      );
      formik.setFieldValue('sections', newSections);
    }
  };

  const getSelectValue = () => {
    const isAll = formik.values.availableTo.organizations.length === orgs?.length;
    if (isAll) return [{ label: 'All' }];

    return orgs
      ?.filter(({ _id }) => formik.values.availableTo.organizations.includes(_id))
      .map(({ _id, name }) => ({ label: name, value: _id }));
  };

  const getOnDeleteMessage = () => {
    if (deleteQuestionModal.sIndex !== undefined && deleteQuestionModal.questionId) {
      const section = formik.values.sections[deleteQuestionModal.sIndex];
      const question = section?.questions?.find((q) => q._id === deleteQuestionModal.questionId);
      if (question?.question) {
        return `Are you sure you want to delete question: ${question.question.substring(0, 50)}...`;
      }
    }
    return 'Are you sure you want to delete this question';
  };

  const handleMoveScenarioBlock = (fromSectionIndex: number, block: SectionBlock, targetSectionIndex: number) => {
    if (fromSectionIndex === targetSectionIndex) return;
    const sections = formik.values.sections.map((s) => ({ ...s, questions: [...s.questions] }));
    const blockIds = new Set(block.questions.map((q) => q._id));
    sections[fromSectionIndex].questions = sections[fromSectionIndex].questions.filter((q) => !blockIds.has(q._id));
    sections[targetSectionIndex].questions = [
      ...sections[targetSectionIndex].questions,
      ...block.questions
    ];
    sections[targetSectionIndex].questions = normalizeSectionQuestions(sections[targetSectionIndex].questions);
    formik.setFieldValue('sections', sections);
  };

  if (exam && scenarioChoice === 'pending' && splitResult.hasSplit) {
    return (
      <ScenarioConsolidationChoiceModal
        evidence={splitResult.evidence}
        consolidationPreview={getConsolidationPreview(exam)}
        onKeep={() => setScenarioChoice('keep')}
        onUpdate={() => setScenarioChoice('update')}
        onClose={onClose}
      />
    );
  }

  return (
    <Modal
      title={exam ? `Edit Exam: ${exam.name}` : 'Create Exam'}
      onClose={() => setIsOpenSecurityModal(true)}
      wrapperClassName="!justify-end !p-0"
      boxClassName="!rounded-r-none max-h-screen h-screen w-[calc(100vw-272px)]"
      contentClassName="overflow-y-auto h-[calc(100%-69px)]"
    >
      {isOpenSecurityModal && (
        <SecurityModal
          close={() => setIsOpenSecurityModal(false)}
          text="Are you sure you want to leave?"
          btnText="Leave"
          onClick={onClose}
        />
      )}

      <form onSubmit={formik.handleSubmit} className="-m-5">
        <div className="p-5 border-b border-secondary">
          <div className="grid gap-4 items-start" style={{ gridTemplateColumns: '1fr 1fr 150px' }}>
            <Field label="Title" formik={formik} name="name" />
            <Select
              formik={formik}
              label="Track"
              name="category"
              options={categoriesOptions}
              value={categoriesOptions?.find((c) => c.value === formik.values.category) || ''}
              onChange={(value: any) => formik.setFieldValue('category', value.value)}
            />
            <Switch
              label="Status"
              isEnable={formik.values.isActive}
              onToggle={() => formik.setFieldValue('isActive', !formik.values.isActive)}
            />
          </div>
          <div className="grid gap-4 items-start mt-5" style={{ gridTemplateColumns: '1fr 1fr 150px' }}>
            <Select
              isMulti={true}
              formik={formik}
              label="Available in organizations"
              name="availableTo.organizations"
              options={orgsOptions}
              value={getSelectValue()}
              onChange={(value: any) => {
                if (value.some((v: any) => v.value === 'all')) {
                  formik.setFieldValue('availableTo.organizations', orgs?.map(({ _id }) => _id) || []);
                } else {
                  formik.setFieldValue(
                    'availableTo.organizations',
                    value.filter((val: any) => val.label !== 'All').map((v: any) => v.value)
                  );
                }

                const oldGroups = formik.values.availableTo.groups;
                formik.setFieldValue(
                  'availableTo.groups',
                  groups?.result
                    .filter(
                      ({ _id, organization }) =>
                        oldGroups.includes(_id) && value.map((v: any) => v.value).includes(organization)
                    )
                    .map(({ _id }) => _id)
                );
              }}
            />
            <Select
              isMulti
              formik={formik}
              label="Available in groups"
              name="availableTo.groups"
              options={groupsOptions}
              value={groups?.result
                .filter(
                  ({ _id, organization }) =>
                    formik.values.availableTo.groups.includes(_id) &&
                    formik.values.availableTo.organizations.includes(organization._id)
                )
                .map(({ _id, name }) => ({ label: name, value: _id }))}
              onChange={(value: any) =>
                formik.setFieldValue(
                  'availableTo.groups',
                  value.map((v: any) => v.value)
                )
              }
            />
            <Field label="Exam duration" formik={formik} name="duration" type="number" step={5} />

            <Field label="Minimum pass percentage" min={50} formik={formik} name={`minPassPercentage`} type="number" />
            <div className="flex flex-col">
              <label className="fieldLabel">
                Notes <span className="text-secondary font-normal text-xs">(max 500 characters)</span>
              </label>
              <textarea
                name="adminNotes"
                placeholder="e.g. Test update on 01/15/25"
                value={formik.values.adminNotes ?? ''}
                onChange={(e) => formik.setFieldValue('adminNotes', e.target.value)}
                rows={3}
                maxLength={500}
                className="w-full border border-secondary rounded px-3 py-2 resize-y min-h-[72px] max-h-[100px] text-sm overflow-y-scroll [scrollbar-width:thin] [scrollbar-color:rgb(156_163_175)_rgb(243_244_246)] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-thumb]:rounded-full"
              />
              {formik.values.adminNotes?.length > 0 && (
                <p className="text-xs text-secondary mt-1">
                  {formik.values.adminNotes.length}/500
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="p-5">
          <button type="button" className="secondaryBtn w-fit mb-5" onClick={onAddSection}>
            <BiPlus />
            Add Section
          </button>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable" type="droppableItem">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {formik.values.sections.map((el, sIndex) => {
                    const sectionQuestions = formik.values.sections[sIndex].questions;
                    const blocks = sectionQuestionsToBlocks(sectionQuestions, scenarioChoice);

                    return (
                      <Draggable key={el.id} draggableId={`item-${el.id}`} index={sIndex}>
                        {(provided) => (
                          <div
                            key={sIndex}
                            ref={provided.innerRef}
                            className="border border-secondary rounded-xl mb-4"
                            {...provided.draggableProps}
                          >
                            <div className="grid items-end gap-4 p-4" style={{ gridTemplateColumns: '20px 1fr auto' }}>
                              <div {...provided.dragHandleProps} className="mb-2 cursor-move">
                                <MdDragIndicator className="w-5 h-5 text-secondary" />
                              </div>
                              <Field label="Section Name" formik={formik} name={`sections[${sIndex}].name`} />

                              <div className="flex items-center gap-2">
                                <Switch
                                  isEnable={formik.values.sections[sIndex].isActive}
                                  onToggle={() =>
                                    formik.setFieldValue(
                                      `sections[${sIndex}].isActive`,
                                      !formik.values.sections[sIndex].isActive
                                    )
                                  }
                                />
                                {formik.values.sections.length > 1 ? (
                                  <>
                                    <button
                                      type="button"
                                      className="iconBtn danger"
                                      onClick={() => {
                                        setIsOpenDeleteSecurityModal(true);
                                      }}
                                    >
                                      <BiTrash />
                                    </button>

                                    {isOpenDeleteSecurityModal && (
                                      <SecurityModal
                                        btnText="Delete"
                                        close={() => setIsOpenDeleteSecurityModal(false)}
                                        onClick={() => {
                                          onRemoveSection(sIndex);
                                          setIsOpenDeleteSecurityModal(false);
                                        }}
                                        text="Are you sure you want to delete this section?"
                                      />
                                    )}
                                  </>
                                ) : null}
                                <FiChevronDown
                                  className={`transition-all duration-200 ease-in-out w-5 h-5 cursor-pointer ${isActiveClass(
                                    collapsedSections === el.id,
                                    'transform rotate-180'
                                  )}`}
                                  onClick={() => setCollapsedSections((p) => (p === el.id ? -1 : el.id))}
                                />
                              </div>
                            </div>
                            {collapsedSections === el.id && (
                              <div className="px-4 pb-4">
                                <Select
                                  formik={formik}
                                  label="Questions"
                                  name={`sections[${sIndex}].questions`}
                                  value={{ label: 'Add Question' }}
                                  options={questionsOptions}
                                  onChange={(value: any) => {
                                    if (value.value === 'create-new-question') {
                                      return setIsOpenCreateQuestionModal({ isOpen: true, index: sIndex });
                                    }

                                    const sections = [...formik.values.sections];
                                    const section = sections[sIndex];
                                    const alreadyInQuestions = [...section.questions];

                                    if (alreadyInQuestions.find((q) => q._id === value.value)) {
                                      alreadyInQuestions.splice(
                                        alreadyInQuestions.findIndex((q) => q._id === value.value),
                                        1
                                      );
                                      section.questions = alreadyInQuestions;
                                      formik.setFieldValue(`sections[${sIndex}].questions`, alreadyInQuestions);
                                      return;
                                    }

                                    const newQuestionData = questions?.result?.find((q) => q._id === value.value);
                                    if (!newQuestionData) return;

                                    const scenarioId = getQuestionScenarioId(newQuestionData);
                                    const existingSectionIndex =
                                      scenarioId !== null
                                        ? getSectionIndexWithScenario(formik.values.sections, scenarioId)
                                        : null;

                                    if (existingSectionIndex !== null && existingSectionIndex !== sIndex) {
                                      setAddQuestionScenarioModal({
                                        question: newQuestionData,
                                        currentSectionIndex: sIndex,
                                        targetSectionIndex: existingSectionIndex,
                                        targetSectionName:
                                          formik.values.sections[existingSectionIndex]?.name || 'Unnamed section',
                                        scenarioTitle: getScenarioTitleFromQuestion(newQuestionData)
                                      });
                                      return;
                                    }

                                    alreadyInQuestions.push(newQuestionData);
                                    section.questions = normalizeSectionQuestions(alreadyInQuestions);
                                    formik.setFieldValue(`sections[${sIndex}].questions`, section.questions);
                                  }}
                                />
                                {blocks.length > 0 ? (
                                  <Droppable droppableId={el.id.toString()} type={`droppableSubItem-${el.id}`}>
                                    {(provided) => (
                                      <ul ref={provided.innerRef} className="mt-4 border border-tertiary rounded-xl">
                                        {blocks.map((block, blockIndex) => {
                                          const draggableId =
                                            block.type === 'block' && block.scenarioId
                                              ? `block-${el.id}-${blockIndex}`
                                              : `subitem-${block.questions[0]._id}`;
                                          return (
                                            <Draggable key={draggableId} draggableId={draggableId} index={blockIndex}>
                                              {(provided) => (
                                                <li
                                                  ref={provided.innerRef}
                                                  className="p-3 border-b border-tertiary last:border-b-0"
                                                  {...provided.draggableProps}
                                                >
                                                  {block.type === 'block' && block.questions.length > 0 ? (
                                                    <ExamScenarioBlock
                                                        sectionId={el.id}
                                                        blockIndex={blockIndex}
                                                        block={block}
                                                        sectionQuestions={sectionQuestions}
                                                        sIndex={sIndex}
                                                        blockDragHandleProps={provided.dragHandleProps ?? undefined}
                                                        onMoveBlock={(sIdx, b) =>
                                                          setMoveBlockModal({ isOpen: true, sIndex: sIdx, block: b })
                                                        }
                                                        onEdit={setQuestionForUpdate}
                                                        onDelete={(sIdx, questionId) =>
                                                          setDeleteQuestionModal({
                                                            isOpen: true,
                                                            sIndex: sIdx,
                                                            questionId
                                                          })
                                                        }
                                                        onDeleteBlock={(sIdx, b) =>
                                                          setDeleteBlockModal({ sIndex: sIdx, block: b })
                                                        }
                                                        onEditScenario={(id) => setScenarioIdForEdit(id)}
                                                    />
                                                  ) : (
                                                    <ExamSingleQuestionBlock
                                                      block={block}
                                                      sectionQuestions={sectionQuestions}
                                                      sIndex={sIndex}
                                                      dragHandleProps={provided.dragHandleProps}
                                                      onEdit={setQuestionForUpdate}
                                                      onDelete={(sIdx, questionId) =>
                                                        setDeleteQuestionModal({
                                                          isOpen: true,
                                                          sIndex: sIdx,
                                                          questionId
                                                        })
                                                      }
                                                    />
                                                  )}
                                                </li>
                                              )}
                                            </Draggable>
                                          );
                                        })}
                                        {provided.placeholder}
                                      </ul>
                                    )}
                                  </Droppable>
                                ) : null}
                              </div>
                            )}
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
        <button
          type="submit"
          className="secondaryBtn fixed top-3 right-4 w-fit"
          disabled={storeIsLoading || updateIsLoading}
        >
          <TfiSave />
          Save Changes
        </button>
      </form>

      {deleteQuestionModal.isOpen ? (
        <SecurityModal
          btnText="Delete"
          className="max-w-sm"
          close={() => setDeleteQuestionModal({ isOpen: false })}
          text={getOnDeleteMessage()}
          onClick={() => {
            const { sIndex, questionId } = deleteQuestionModal;
            if (sIndex !== undefined && questionId) {
              const sections = formik.values.sections.map((s) => ({ ...s, questions: [...s.questions] }));
              const section = sections[sIndex];
              const idx = section?.questions?.findIndex((q) => q._id === questionId);
              if (idx !== undefined && idx >= 0) {
                section.questions.splice(idx, 1);
                formik.setFieldValue(`sections[${sIndex}].questions`, section.questions);
              }
              setDeleteQuestionModal({ isOpen: false });
            }
          }}
        />
      ) : null}
      {deleteBlockModal && (
        <SecurityModal
          btnText="Delete"
          className="max-w-sm"
          close={() => setDeleteBlockModal(null)}
          text={`Delete this scenario block? ${deleteBlockModal.block.questions.length} question(s) will be removed from the section.`}
          onClick={() => {
            const { sIndex, block } = deleteBlockModal;
            const blockIds = new Set(block.questions.map((q) => q._id));
            const sections = formik.values.sections.map((s) => ({ ...s, questions: [...s.questions] }));
            sections[sIndex].questions = sections[sIndex].questions.filter((q) => !blockIds.has(q._id));
            formik.setFieldValue(`sections[${sIndex}].questions`, sections[sIndex].questions);
            setDeleteBlockModal(null);
          }}
        />
      )}
      {moveBlockModal && (
        <MoveScenarioBlockModal
          sections={formik.values.sections}
          currentSectionId={formik.values.sections[moveBlockModal.sIndex]?.id ?? 0}
          block={moveBlockModal.block}
          onMove={(targetSectionIndex) =>
            handleMoveScenarioBlock(moveBlockModal.sIndex, moveBlockModal.block, targetSectionIndex)
          }
          onClose={() => setMoveBlockModal(null)}
        />
      )}
      {addQuestionScenarioModal && (
        <AddQuestionScenarioChoiceModal
          scenarioTitle={addQuestionScenarioModal.scenarioTitle}
          targetSectionName={addQuestionScenarioModal.targetSectionName}
          onKeepInCurrentSection={() => {
            const { question, currentSectionIndex } = addQuestionScenarioModal;
            const sections = formik.values.sections.map((s) => ({ ...s, questions: [...s.questions] }));
            sections[currentSectionIndex].questions.push(question);
            sections[currentSectionIndex].questions = normalizeSectionQuestions(
              sections[currentSectionIndex].questions
            );
            formik.setFieldValue('sections', sections);
            setAddQuestionScenarioModal(null);
          }}
          onMoveToScenarioSection={() => {
            const { question, targetSectionIndex } = addQuestionScenarioModal;
            const sections = formik.values.sections.map((s) => ({ ...s, questions: [...s.questions] }));
            sections[targetSectionIndex].questions.push(question);
            sections[targetSectionIndex].questions = normalizeSectionQuestions(
              sections[targetSectionIndex].questions
            );
            formik.setFieldValue('sections', sections);
            setAddQuestionScenarioModal(null);
          }}
          onClose={() => setAddQuestionScenarioModal(null)}
        />
      )}
      {(isOpenCreateQuestionModal.isOpen || questionForUpdate) && (
        <QuestionFormModal
          onClose={() => {
            setIsOpenCreateQuestionModal({ isOpen: false });
            setQuestionForUpdate(null);
          }}
          selectCreatedQuestion={(q: Question) => {
            const index = isOpenCreateQuestionModal.index;
            const section = index !== undefined ? formik.values.sections[index] : undefined;
            if (section) {
              formik.setFieldValue(
                `sections[${index}].questions`,
                normalizeSectionQuestions([...section.questions, q])
              );
            }
          }}
          question={questionForUpdate}
          refetch={refetchQuestions}
        />
      )}
      {scenarioIdForEdit && scenarioForEdit && (
        <ScenarioFormModal
          scenario={scenarioForEdit}
          refetch={() => {
            refetch();
            refetchQuestions();
          }}
          onClose={() => setScenarioIdForEdit(null)}
        />
      )}
    </Modal>
  );
};
